import "./types";

/** Same host resolution as requests.ts — relative in dev (proxy), absolute in prod. */
const ONBOARDING_API_BASE =
  process.env.NODE_ENV === "production"
    ? (process.env.REACT_APP_SERVICE_URL || "").replace(/\/$/, "")
    : "";

let initialized = false;
let initPromise: Promise<void> | null = null;

function readConfig() {
  return {
    appId: (process.env.REACT_APP_SUPPORTKIT_APP_ID || "").trim(),
    apiKey: (process.env.REACT_APP_SUPPORTKIT_API_KEY || "").trim(),
    apiBaseUrl: (process.env.REACT_APP_SUPPORTKIT_API_BASE_URL || "").trim(),
    cdnUrl: (process.env.REACT_APP_SUPPORTKIT_CDN_URL || "").trim(),
    primaryColor: (
      process.env.REACT_APP_SUPPORTKIT_PRIMARY_COLOR || "#10b981"
    ).trim(),
  };
}

function loadSupportKitScript(cdnUrl: string): Promise<void> {
  if (window.SupportKit?.init) {
    return Promise.resolve();
  }

  const existing = document.querySelector<HTMLScriptElement>(
    'script[data-supportkit="cdn"]',
  );
  if (existing) {
    return new Promise((resolve, reject) => {
      if (window.SupportKit?.init) {
        resolve();
        return;
      }
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener(
        "error",
        () => reject(new Error("[SupportKit] CDN script failed to load")),
        { once: true },
      );
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = cdnUrl;
    script.async = true;
    script.dataset.supportkit = "cdn";
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error(`[SupportKit] Failed to load CDN script: ${cdnUrl}`));
    document.body.appendChild(script);
  });
}

function configureSupportKit(config: ReturnType<typeof readConfig>): void {
  const SupportKit = window.SupportKit;
  if (!SupportKit?.init) {
    throw new Error("[SupportKit] SDK loaded but window.SupportKit.init is missing");
  }

  SupportKit.init({
    appId: config.appId,
    apiKey: config.apiKey,
    apiBaseUrl: config.apiBaseUrl,
    session: {
      storage: "local",
    },
    context: {
      appVersion: "0.1.0",
      environment:
        process.env.NODE_ENV === "production" ? "production" : "development",
    },
    capture: {
      screenshot: true,
      recordingSeconds: 20,
      network: { enabled: true, maxEntries: 100, includeBodies: "errors" },
      pageUrl: true,
    },
    theme: {
      primaryColor: config.primaryColor,
      position: "bottom-right",
      mode: "system",
      allowPositionChange: true,
    },
    auth: {
      meUrl: `${ONBOARDING_API_BASE}/OnBoarding/username`,
      mapUser: (user) => {
        const first = String(user.firstName ?? "").trim();
        const last = String(user.lastName ?? "").trim();
        const name =
          [first, last].filter(Boolean).join(" ") ||
          String(user.username ?? user.email ?? "").trim();
        const email = String(user.email ?? "").trim();
        const id = user.id ?? user.username ?? email ?? "anonymous";

        const tenants = Array.isArray(user.tenants) ? user.tenants : [];
        const firstTenant = tenants[0] as { tenantId?: unknown } | undefined;
        const customer = user.customer as { id?: unknown } | undefined;
        const tenantId = firstTenant?.tenantId ?? customer?.id ?? undefined;

        return {
          id: id as string | number,
          name,
          email,
          ...(tenantId !== undefined && tenantId !== null
            ? { tenantId: tenantId as string | number }
            : {}),
        };
      },
    },
  });
}

/**
 * Load the SupportKit CDN script and initialize once at app root.
 * Requires REACT_APP_SUPPORTKIT_* env vars; skips silently when unset.
 */
export function initSupportKit(): Promise<void> {
  if (initialized) return Promise.resolve();
  if (initPromise) return initPromise;

  const config = readConfig();
  const missing = (
    [
      ["REACT_APP_SUPPORTKIT_APP_ID", config.appId],
      ["REACT_APP_SUPPORTKIT_API_KEY", config.apiKey],
      ["REACT_APP_SUPPORTKIT_API_BASE_URL", config.apiBaseUrl],
      ["REACT_APP_SUPPORTKIT_CDN_URL", config.cdnUrl],
    ] as const
  )
    .filter(([, value]) => !value)
    .map(([name]) => name);

  if (missing.length > 0) {
    console.warn(
      `[SupportKit] Skipping init; missing env: ${missing.join(", ")}`,
    );
    return Promise.resolve();
  }

  initPromise = loadSupportKitScript(config.cdnUrl)
    .then(() => {
      if (initialized) return;
      configureSupportKit(config);
      initialized = true;
    })
    .catch((error: unknown) => {
      initPromise = null;
      console.warn(
        "[SupportKit] Init failed:",
        error instanceof Error ? error.message : error,
      );
    });

  return initPromise;
}
